import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { Dialog, makeStyles } from '@material-ui/core';

import { Box, Text, ButtonFilled as Button, ButtonText, Divider, Checkbox } from 'Components/Base';

import Carousel from '../Carousel';

import ContentContainer from './ContentContainer';
import DetailContainer from './DetailContainer';
import ActionButtonContainer from './ActionButtonContainer';
import ImageBanner from './ImageBanner';

const useModalStyle = makeStyles({
  paper: {
    margin: '16px',
    width: '343px',
    height: '715px',
    borderRadius: '8px',
    padding: '0',
  },
});

/**
 * @param {object} props
 * @param {boolean} props.doNotShowAgain
 * @param {() => void} props.onCheckDoNotShow
 */
const NotShowCheckbox = ({ doNotShowAgain, onCheckDoNotShow, ...props }) => {
  const { colors } = useTheme();
  return (
    <Box textAlign="center" {...props}>
      <Checkbox
        color={colors.greyscale.grey4}
        checkedColor={true}
        checked={doNotShowAgain}
        onChange={(e, checked) => {
          onCheckDoNotShow(checked);
        }}
      />
      <Text as="span" pr={3} verticalAlign="middle">
        ไม่ต้องแสดงหน้านี้อีก
      </Text>
    </Box>
  );
};

NotShowCheckbox.propTypes = {
  doNotShowAgain: PropTypes.bool,
  onCheckDoNotShow: PropTypes.func,
};

const FeatureAnnouncement = ({
  isCarouselAutoPlay,
  announcements,
  onBannerClick,
  onDetailClick,
  onCompleted,
  handleLastAnnouncementSlide,
  fallbackBannerUrl,
  ...props
}) => {
  const { paper } = useModalStyle();
  const [activeIndex, setActiveIndex] = useState(0);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const isLastFeature = activeIndex === announcements.length - 1;
  const dataTestIdPrefix = 'announcement';

  useEffect(() => {
    if (isLastFeature && typeof handleLastAnnouncementSlide === 'function') {
      handleLastAnnouncementSlide(true);
    }
  }, [isLastFeature]);

  return (
    <Dialog {...props} scroll={'body'} classes={{ paper: paper }} disableEscapeKeyDown>
      <Box height={'100%'} id="feature-announcement-popup">
        <Carousel
          slickOptions={{
            dots: true,
            arrows: true,
            centerMode: false,
            autoplay: isCarouselAutoPlay ?? true,
            autoplaySpeed: 4000,
          }}
          infinite={false}
          height="100%"
          listLength={announcements.length}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
        >
          {announcements.map(({ imageUrl, imageAlt, title, description, button }, index) => {
            const buttonText = button?.title;
            const buttonLink = button?.href;
            const hasButton = button && button?.title && button?.href;
            const imageNo = index + 1;

            return (
              <Fragment key={index}>
                <ImageBanner
                  data-testid={`img-${imageNo}-${dataTestIdPrefix}-redirect-to-external-link`}
                  src={imageUrl}
                  alt={imageAlt}
                  onClick={(e) => onBannerClick(e, buttonLink)}
                  onError={(e) => {
                    console.error(`${e.target.src} 404 not found`);
                    e.target.onerror = null;
                    e.target.src = fallbackBannerUrl;
                  }}
                />
                <Divider my={0} borderColor="#E6E6E6" style={{ borderStyle: 'solid' }} />
                <ContentContainer>
                  <DetailContainer>
                    <Text textAlign="center" textStyle={'heading3'}>
                      {title}
                    </Text>
                    <Text textAlign="center" mt={4}>
                      {description}
                    </Text>
                  </DetailContainer>
                  <ActionButtonContainer>
                    {hasButton && (
                      <Button
                        data-testid={`btn-${dataTestIdPrefix}-redirect-to-external-link`}
                        mt={0}
                        p={2}
                        width={1}
                        borderRadius="8px"
                        onClick={(e) => onDetailClick(e, buttonLink)}
                      >
                        {buttonText}
                      </Button>
                    )}
                    <ButtonText
                      p={0}
                      mt={4}
                      width={1}
                      data-testid={`btn-${dataTestIdPrefix}-close-popup-announcement`}
                      onClick={() => onCompleted(doNotShowAgain)}
                    >
                      ปิด
                    </ButtonText>
                    <NotShowCheckbox
                      doNotShowAgain={doNotShowAgain}
                      onCheckDoNotShow={setDoNotShowAgain}
                      mt={2}
                      data-testid={`checkbox-${dataTestIdPrefix}-do-not-show-again`}
                    />
                  </ActionButtonContainer>
                </ContentContainer>
              </Fragment>
            );
          })}
        </Carousel>
      </Box>
    </Dialog>
  );
};

FeatureAnnouncement.propTypes = {
  announcements: PropTypes.array,
  containerProps: PropTypes.object,
  onBannerClick: PropTypes.func,
  onDetailClick: PropTypes.func,
  handleLastAnnouncementSlide: PropTypes.func,
  onCompleted: PropTypes.func,
  fallbackBannerUrl: PropTypes.string,
};
FeatureAnnouncement.defaultProps = {
  announcements: [],
  onBannerClick: () => {},
  onCompleted: (doNotShowAgain) => {},
  onDetailClick: () => {},
  fallbackBannerUrl: '/assets/images/defaultCourseThumbnail/empty-thumbnail-ANY.png',
};

export default FeatureAnnouncement;
